// TextField — the canonical text-input component.
//
// Wraps a native <input> with label / hint / error / icon slots /
// prefix-suffix text slots, all properly ARIA-wired. The visual
// contract (border, focus, hover, error background) comes from
// global.css and applies to ALL inputs anywhere — TextField is the
// ergonomic layer on top.
//
// Slot anatomy (left -> right):
//
//   ┌──────────────────────────────────────────────────────┐
//   │ prefix │ icon │   input text          │ icon │ suffix │
//   └──────────────────────────────────────────────────────┘
//
//   prefix / suffix: TEXT slot (e.g. "+977", "@school.com", ".pdf")
//                    rendered with a subtle divider; readable label
//                    sized at the input's font size.
//   leadingIcon / trailingIcon: GLYPH slot (icon component) rendered
//                               INSIDE the input padding; muted colour.
//
// Variants:
//   outlined (default) — bordered input on white
//   filled             — soft grey filled background, no border;
//                        focus expands to white + brand ring
//   code               — bigger, monospace, letter-spaced for OTP
//
// Use TextField for: text, email, tel, password, number, url, search.
// Use <Field> directly for: select, textarea, or custom controls.

import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  useId,
} from "react";

import { tokens } from "../tokens/tokens";

type InputType =
  | "text"
  | "email"
  | "tel"
  | "password"
  | "number"
  | "url"
  | "search";

export interface TextFieldProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    "type" | "size" | "prefix"
  > {
  /** Visible label above the input. */
  label: string;
  /** Optional helper text under the input (replaced by error if present). */
  hint?: string;
  /** Error message — input flips to invalid state. */
  error?: string;
  /** HTML input type. Defaults to "text". */
  type?: InputType;
  /** Mark required (* on label + sets required on input). */
  required?: boolean;
  /** Text rendered LEFT of the input, with a divider. e.g. "+977". */
  prefix?: ReactNode;
  /** Text rendered RIGHT of the input, with a divider. e.g. ".pdf". */
  suffix?: ReactNode;
  /** Glyph rendered inside the input, on the left. */
  leadingIcon?: ReactNode;
  /** Glyph rendered inside the input, on the right. */
  trailingIcon?: ReactNode;
  /** Visual variant. Default "outlined". */
  variant?: "outlined" | "filled" | "code";
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  function TextField(
    {
      label,
      hint,
      error,
      required,
      prefix,
      suffix,
      leadingIcon,
      trailingIcon,
      variant = "outlined",
      type = "text",
      style,
      ...inputProps
    },
    ref,
  ) {
    const id = useId();
    const hintId = hint && !error ? `${id}-hint` : undefined;
    const errorId = error ? `${id}-error` : undefined;
    const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;

    // Per-variant input styling. Other states (hover, focus, error,
    // disabled, autofill) come from global.css and layer on top.
    const variantInputStyle: React.CSSProperties =
      variant === "filled"
        ? filledInputStyle
        : variant === "code"
          ? codeInputStyle
          : {};

    const groupStyle: React.CSSProperties =
      variant === "filled" ? filledGroupStyle : outlinedGroupStyle;

    const inputStyle: React.CSSProperties = {
      width: "100%",
      // Prefix/suffix sit outside the input visually but inside the same
      // grouped border. We push the input's padding off so the divider
      // bridges to the input edge cleanly.
      borderTopLeftRadius: prefix ? 0 : undefined,
      borderBottomLeftRadius: prefix ? 0 : undefined,
      borderTopRightRadius: suffix ? 0 : undefined,
      borderBottomRightRadius: suffix ? 0 : undefined,
      paddingLeft: leadingIcon ? 44 : undefined,
      paddingRight: trailingIcon ? 44 : undefined,
      // When inside a grouped wrap (prefix/suffix), the outer wrap owns
      // the visible border; drop the input's so they don't double up.
      border: prefix || suffix ? 0 : undefined,
      boxShadow: prefix || suffix ? "none" : undefined,
      ...variantInputStyle,
      ...style,
    };

    const inner = (
      <div style={inputWrapStyle}>
        {leadingIcon && (
          <span aria-hidden="true" style={leadingIconStyle}>
            {leadingIcon}
          </span>
        )}
        <input
          {...inputProps}
          id={id}
          ref={ref}
          type={type}
          required={required}
          aria-describedby={describedBy}
          aria-invalid={error ? "true" : undefined}
          aria-errormessage={errorId}
          style={inputStyle}
        />
        {trailingIcon && (
          <span aria-hidden="true" style={trailingIconStyle}>
            {trailingIcon}
          </span>
        )}
      </div>
    );

    const grouped = prefix || suffix ? (
      <div
        style={groupStyle}
        data-has-error={error ? "true" : undefined}
        className="ds-textfield-group"
      >
        {prefix && (
          <span style={affixStyle} aria-hidden="true">
            {prefix}
          </span>
        )}
        {inner}
        {suffix && (
          <span style={{ ...affixStyle, borderLeft: `1px solid ${tokens.color.semantic.border}`, borderRight: 0 }} aria-hidden="true">
            {suffix}
          </span>
        )}
      </div>
    ) : inner;

    return (
      <div style={wrapperStyle}>
        <style>{groupCSS}</style>
        <label htmlFor={id} style={labelStyle}>
          {label}
          {required && (
            <span aria-hidden="true" style={requiredMarkStyle}>
              {" "}*
            </span>
          )}
        </label>
        {grouped}
        {hint && !error && (
          <p id={hintId} style={hintStyle}>
            {hint}
          </p>
        )}
        {error && (
          <p id={errorId} role="alert" style={errorTextStyle}>
            {error}
          </p>
        )}
      </div>
    );
  },
);

// ── Styles ────────────────────────────────────────────────────────────

const wrapperStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: tokens.space[2],
};
const labelStyle: React.CSSProperties = {
  fontSize: tokens.type.size.sm,
  fontWeight: tokens.type.weight.medium,
  color: tokens.color.semantic.text,
  letterSpacing: "-0.005em",
};
const requiredMarkStyle: React.CSSProperties = {
  color: tokens.color.danger[500],
};

const inputWrapStyle: React.CSSProperties = {
  position: "relative",
  display: "flex",
  alignItems: "center",
  flex: 1,
  minWidth: 0,
};

const filledGroupStyle: React.CSSProperties = {};
const outlinedGroupStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "stretch",
  background: "#ffffff",
  border: `1.5px solid #e2e8f0`,
  borderRadius: 12,
  boxShadow: "inset 0 1px 0 rgba(15, 23, 42, 0.02)",
  transition: "border-color 160ms ease, box-shadow 160ms ease, background 160ms ease",
  overflow: "hidden",
};

// CSS for the grouped (prefix/suffix) wrap state — hover, focus-within,
// invalid. Mirrors the global input rules so the group reads as one
// continuous control.
const groupCSS = `
@media (hover: hover) {
  .ds-textfield-group:hover {
    border-color: #cbd5e1;
    background: #fafbfc;
  }
}
.ds-textfield-group:focus-within {
  outline: none;
  border-color: #dc2626;
  background: #ffffff;
  box-shadow: 0 0 0 4px rgba(220, 38, 38, 0.12);
}
.ds-textfield-group[data-has-error="true"] {
  border-color: #dc2626;
  background: #fef2f2;
}
.ds-textfield-group[data-has-error="true"]:focus-within {
  box-shadow: 0 0 0 4px rgba(220, 38, 38, 0.22);
}
`;

const affixStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  padding: `0 ${tokens.space[4]}`,
  background: tokens.color.semantic.surfaceAlt,
  color: tokens.color.semantic.textMuted,
  fontSize: tokens.type.size.base,
  fontWeight: tokens.type.weight.medium,
  letterSpacing: "0.01em",
  whiteSpace: "nowrap",
  borderRight: `1px solid ${tokens.color.semantic.border}`,
};

const leadingIconStyle: React.CSSProperties = {
  position: "absolute",
  left: 16,
  top: "50%",
  transform: "translateY(-50%)",
  color: tokens.color.semantic.textMuted,
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  fontSize: 18,
  lineHeight: 1,
};
const trailingIconStyle: React.CSSProperties = {
  ...leadingIconStyle,
  left: undefined,
  right: 16,
};

const hintStyle: React.CSSProperties = {
  fontSize: tokens.type.size.xs,
  color: tokens.color.semantic.textMuted,
  lineHeight: 1.5,
};
const errorTextStyle: React.CSSProperties = {
  fontSize: tokens.type.size.xs,
  color: tokens.color.danger[700],
  fontWeight: tokens.type.weight.medium,
  lineHeight: 1.5,
};

const filledInputStyle: React.CSSProperties = {
  background: tokens.color.semantic.surfaceAlt,
  border: 0,
  boxShadow: "none",
};
const codeInputStyle: React.CSSProperties = {
  textAlign: "center",
  fontFamily: tokens.type.family.mono,
  fontSize: tokens.type.size["2xl"],
  fontWeight: tokens.type.weight.semibold,
  letterSpacing: "0.32em",
  minHeight: tokens.size.tap56,
  paddingLeft: 0,
  paddingRight: 0,
};
